import { CartAddEvent } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

/**
 * <shampoo-upsell> — cart-drawer / cart-page reward ladder (Layout B).
 *
 * One segmented progress bar with three milestone nodes that light up as the
 * cart climbs:
 *   €50  → free shipping   (suggests an in-stock shampoo to bridge the gap)
 *   €100 → free shampoo    (INFORMATIONAL — the shop adds it to the order when
 *                           packing; it is NOT a cart line item and not discounted)
 *   €150 → extra −5 %       (the store's automatic discount)
 *
 * SEO/async: the element renders EMPTY server-side and hydrates here. The
 * shampoo catalog AND the gift shampoo are fetched once via the Section
 * Rendering API (`/?section_id=shampoo-upsell-data`) the first time the cart is
 * shown, then cached — no shampoo data ships in any page's HTML.
 *
 * Liquid emits only integers (see snippets/cart-summary.liquid):
 *   data-cart-total      post-discount total, cents   (free-shipping node + tier-1 math)
 *   data-subtotal        pre-discount subtotal, cents (gift + −5% nodes)
 *   data-discount-rate   cart % discount fraction      (tier-1 bridging math)
 *
 * The whole widget is suppressed in Liquid when the cart contains a gift card.
 */

const FREE_SHIP = 5000; // €50.00 — free shipping
const GIFT = 10000; //      €100.00 — free shampoo (informational)
const DISC5 = 15000; //     €150.00 — extra −5%
const SECTION_ID = 'shampoo-upsell-data';
const COLLECTION_PATH = 'collections/samponi-za-lase';

let sectionPromise = null; // session cache for the fetched section (catalog + gift)
let lastPickId = null; // keep the tier-1 shampoo stable across re-renders while still eligible

function shopRoot() {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

function money(cents) {
  return `€${(cents / 100).toFixed(2).replace('.', ',')}`;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/** Fetch + parse the section once; resolves to { catalog: [], gift: {} | null }. */
function loadSection() {
  if (!sectionPromise) {
    const url = `${shopRoot()}?section_id=${SECTION_ID}`;
    sectionPromise = fetch(url, { headers: { Accept: 'text/html' } })
      .then((res) => (res.ok ? res.text() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const catalogNode = doc.querySelector('[data-shampoo-upsell-catalog]');
        const giftNode = doc.querySelector('[data-gift-upsell-product]');
        return {
          catalog: catalogNode ? JSON.parse(catalogNode.textContent) : [],
          gift: giftNode ? JSON.parse(giftNode.textContent) : null,
        };
      })
      .catch(() => {
        sectionPromise = null; // allow a retry the next time the cart opens
        return { catalog: [], gift: null };
      });
  }
  return sectionPromise;
}

class ShampooUpsell extends HTMLElement {
  static observedAttributes = ['data-cart-total', 'data-subtotal', 'data-discount-rate', 'data-has-code', 'data-shampoo-paused'];

  /** @type {Element | null} */
  #drawer = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {HTMLElement | null} */
  #box = null;
  /** @type {HTMLElement | null} */
  #extra = null;
  /** @type {HTMLElement | null} */
  #root = null;
  #isCartPage = false;
  #everOpened = false;
  #lastRenderEmpty = true;
  #updateSeq = 0;

  #onDrawerOpen = () => {
    this.#everOpened = true;
    this.#update();
  };

  connectedCallback() {
    this.#drawer = this.closest('cart-drawer-component');
    this.#isCartPage = !this.#drawer;
    this.#drawer?.addEventListener(DialogOpenEvent.eventName, this.#onDrawerOpen);

    // Each cart/discount change morphs this element back to its empty
    // server-rendered state, wiping our JS render. A childList observer
    // re-hydrates whenever a morph clears a card we had rendered. (The discount
    // path morphs twice; the 2nd async render fires no follow-up trigger.)
    this.#observer = new MutationObserver(() => this.#rehydrate());
    this.#observer.observe(this, { childList: true });

    // Drawer context defers to first open; cart-page context hydrates now.
    if (this.#isCartPage || this.#drawerOpen()) this.#onDrawerOpen();
  }

  disconnectedCallback() {
    this.#drawer?.removeEventListener(DialogOpenEvent.eventName, this.#onDrawerOpen);
    this.#observer?.disconnect();
    this.#drawer = null;
    this.#observer = null;
    this.#box = this.#extra = this.#root = null;
  }

  attributeChangedCallback() {
    // Cart total / subtotal changed. Re-render once shown. NOT gated on the live
    // dialog-open state — that briefly flips false mid-morph.
    if (this.#everOpened) this.#update();
  }

  // A morph cleared our content. Re-render only if we had shown something (not an
  // intentional empty), so we never loop on the nothing-to-show case.
  #rehydrate() {
    if (this.childElementCount > 0 || this.#lastRenderEmpty) return;
    if (this.#everOpened) this.#update();
  }

  #drawerOpen() {
    return Boolean(this.closest('dialog')?.open);
  }

  #readState() {
    const total = parseInt(this.dataset.cartTotal ?? '', 10) || 0;
    const subtotal = parseInt(this.dataset.subtotal ?? '', 10) || 0;
    const hasCode = this.dataset.hasCode === '1';
    // With a code, milestones track the POST-DISCOUNT total (what's actually paid).
    // Without one, they track the subtotal — which equals the total below €150 and
    // matches the automatic −5% (subtotal-keyed) trigger at €150.
    const progress = hasCode ? total : subtotal;
    return {
      total,
      subtotal,
      progress,
      hasCode,
      shampooPaused: this.dataset.shampooPaused === '1',
      discountRate: parseFloat(this.dataset.discountRate ?? '') || 0,
      shipDone: progress >= FREE_SHIP,
      giftEarned: progress >= GIFT,
    };
  }

  async #update() {
    const seq = ++this.#updateSeq;
    const state = this.#readState();

    // Render the bar/nodes/nudge synchronously from the dataset (no await), then
    // fill the card area once the section resolves (cached after the first open).
    this.#renderLadder(state);
    if (state.shampooPaused) return; // No shampoo cards or catalog request during the promotion.

    const section = await loadSection();
    if (seq !== this.#updateSeq) return; // superseded by a newer update while awaiting

    this.#fillExtras(state, section);
  }

  // ---- rendering -----------------------------------------------------------
  #renderLadder(state) {
    const root = el('div', 'rwb');
    const top = el('div', 'rwb__top');

    // Three milestones normally; collapse to two (drop the −5%) when a discount
    // code is applied — such codes are higher than 5% and can't stack with it.
    const milestones = [
      { amt: '€50', txt: 'dostava', kind: 'ship', threshold: FREE_SHIP },
    ];
    if (!state.shampooPaused) {
      milestones.push({ amt: '€100', txt: 'darilo', kind: 'gift', threshold: GIFT });
    }
    if (!state.hasCode) {
      milestones.push({ amt: '€150', txt: '−5 %', kind: 'disc', threshold: DISC5 });
    }
    const lastThreshold = milestones[milestones.length - 1].threshold;
    const done = milestones.map((m) => state.progress >= m.threshold);
    const nextIndex = done.findIndex((d) => !d); // -1 once everything is unlocked

    const seg = el('div', 'seg');
    seg.setAttribute('aria-hidden', 'true');
    const fill = el('div', 'seg__fill');
    fill.style.width = `${Math.min(100, Math.max(0, (state.progress / lastThreshold) * 100))}%`;
    seg.appendChild(fill);
    milestones.forEach((m, i) => {
      let cls = 'seg__node';
      if (done[i]) cls += ' is-done';
      else if (i === nextIndex) cls += ' is-next';
      const dot = el('div', cls);
      dot.style.left = `${(m.threshold / lastThreshold) * 100}%`;
      seg.appendChild(dot);
    });
    top.appendChild(seg);

    const labels = el('div', 'seg__labels');
    milestones.forEach((m, i) => {
      let cls = 'seg__lab';
      if (done[i]) cls += ' is-done';
      else if (i === nextIndex) cls += ' is-next';
      const lab = el('div', cls);
      lab.style.left = `${(m.threshold / lastThreshold) * 100}%`;
      lab.appendChild(el('b', null, m.amt));
      lab.appendChild(document.createTextNode(done[i] ? `${m.txt} ✓` : m.txt));
      labels.appendChild(lab);
    });
    top.appendChild(labels);

    // Only show the nudge while there is a next milestone. Once everything is
    // unlocked, the all-green bar + earned card say it — no closing line/separator.
    if (nextIndex >= 0) {
      const nextWrap = el('div', 'rwb-next');
      const nudge = el('div', 'rwb-nudge');
      nudge.setAttribute('role', 'status');
      nudge.setAttribute('aria-live', 'polite');
      nudge.appendChild(this.#nudgeContent(milestones, nextIndex, state));
      nextWrap.appendChild(nudge);
      top.appendChild(nextWrap);
    }

    const extra = el('div', 'rwb-extra');
    extra.hidden = true;
    top.appendChild(extra);

    root.appendChild(top);

    const box = el('div', 'shampoo-upsell__box');
    box.hidden = true;
    root.appendChild(box);

    this.#root = root;
    this.#box = box;
    this.#extra = extra;
    this.#lastRenderEmpty = false;
    this.replaceChildren(root);
  }

  #nudgeContent(milestones, nextIndex, state) {
    const span = document.createElement('span');
    const m = milestones[nextIndex];
    const remaining = m.threshold - state.progress;
    const tail = m.kind === 'ship' ? ' do brezplačne dostave' : m.kind === 'gift' ? ' do brezplačnega šampona' : ' do −5 % na vse';
    span.append('Še ', el('strong', null, money(remaining)), tail);
    return span;
  }

  #fillExtras(state, section) {
    if (!this.#box || !this.#extra) return;
    this.#box.replaceChildren();
    this.#box.hidden = true;
    this.#extra.replaceChildren();
    this.#extra.hidden = true;
    this.#root?.classList.remove('has-card');

    if (!state.shipDone) {
      // Tier 1 — suggest a shampoo that bridges to free shipping.
      const card = this.#tier1Card(section.catalog, state);
      if (card) {
        this.#box.appendChild(card);
        this.#box.hidden = false;
        this.#root?.classList.add('has-card');
      }
    } else {
      // €50+ — show the shampoo: a muted GOAL below €100, an EARNED card at €100+.
      const card = this.#giftCard(section.gift, state.giftEarned);
      if (card) {
        this.#box.appendChild(card);
        this.#box.hidden = false;
        this.#root?.classList.add('has-card');
      }
    }
  }

  #tier1Card(catalog, state) {
    const remaining = FREE_SHIP - state.total;
    if (remaining <= 0) return null;

    // A shampoo is eligible only if its worst-case contribution (price minus the
    // cart's % discount, rounded UP) still clears the gap — so the suggestion can
    // never land the post-discount total below €50.
    const rate = state.discountRate;
    const eligible = catalog.filter((s) => s.price - Math.ceil(s.price * rate) >= remaining);
    if (!eligible.length) return null;

    let pick = eligible.find((s) => s.id === lastPickId);
    if (!pick) {
      pick = eligible[Math.floor(Math.random() * eligible.length)];
      lastPickId = pick.id;
    }

    const frag = document.createDocumentFragment();
    frag.appendChild(el('p', 'shampoo-upsell__title', 'Dodajte šampon in dostava je brezplačna'));
    frag.appendChild(this.#productCard(pick, rate));
    const more = el('a', 'shampoo-upsell__more', 'ali izberite šampon po želji →');
    more.href = `${shopRoot()}${COLLECTION_PATH}`;
    frag.appendChild(more);
    return frag;
  }

  /** The tier-1 product card with an inline "Dodaj" button. */
  #productCard(s, rate) {
    const discounted = rate > 0 ? s.price - Math.round(s.price * rate) : s.price;
    const hasDiscount = discounted < s.price;

    const card = el('div', 'shampoo-upsell__card');
    const link = el('a', 'shampoo-upsell__link');
    link.href = s.url;
    link.setAttribute('aria-label', `Odprite izdelek ${s.title}`);
    card.appendChild(link);

    if (s.img) {
      const thumb = el('img', 'shampoo-upsell__thumb');
      thumb.src = s.img;
      thumb.alt = '';
      thumb.loading = 'lazy';
      thumb.width = 46;
      thumb.height = 46;
      card.appendChild(thumb);
    }

    const info = el('span', 'shampoo-upsell__info');
    info.appendChild(el('span', 'shampoo-upsell__name', s.title));
    if (s.sub) info.appendChild(el('span', 'shampoo-upsell__sub', s.sub));
    card.appendChild(info);

    const right = el('span', 'shampoo-upsell__right');
    const prices = el('span', 'shampoo-upsell__prices');
    if (hasDiscount) prices.appendChild(el('span', 'shampoo-upsell__price-original', money(s.price)));
    prices.appendChild(el('span', `shampoo-upsell__price${hasDiscount ? ' shampoo-upsell__price--sale' : ''}`, money(discounted)));

    const add = el('button', 'shampoo-upsell__add', '+ Dodaj');
    add.type = 'button';
    const priceLabel = hasDiscount ? `${money(discounted)}, redno ${money(s.price)}` : money(s.price);
    add.setAttribute('aria-label', `Dodaj ${s.title} (${priceLabel}) v košarico`);
    add.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.#add(s.id, add);
    });
    right.append(prices, add);
    card.appendChild(right);
    return card;
  }

  /**
   * The free-shampoo card — informational (the shop adds it to the order).
   * @param {boolean} earned - true once the cart has reached €100 (achieved look);
   *   false below €100 (muted goal look).
   */
  #giftCard(gift, earned) {
    if (!gift) return null;

    const frag = document.createDocumentFragment();
    const card = el('div', `shampoo-upsell__card gift-card${earned ? ' gift-card--earned' : ''}`);
    const link = el('a', 'shampoo-upsell__link');
    link.href = gift.url;
    link.setAttribute('aria-label', `Odprite izdelek ${gift.title}`);
    card.appendChild(link);

    if (gift.img) {
      const thumb = el('img', 'shampoo-upsell__thumb');
      thumb.src = gift.img;
      thumb.alt = '';
      thumb.loading = 'lazy';
      thumb.width = 46;
      thumb.height = 46;
      card.appendChild(thumb);
    }

    const info = el('span', 'shampoo-upsell__info');
    info.appendChild(el('span', 'shampoo-upsell__name', gift.title));
    info.appendChild(el('span', 'shampoo-upsell__sub', earned ? 'Vaše brezplačno darilo' : 'Brezplačno darilo nad 100 €'));
    card.appendChild(info);

    const right = el('span', 'shampoo-upsell__right');
    right.appendChild(el('span', `gift-badge${earned ? ' gift-badge--earned' : ''}`, earned ? 'Darilo ✓' : 'Darilo'));
    card.appendChild(right);

    frag.appendChild(card);
    return frag;
  }

  // ---- tier-1 cart add -----------------------------------------------------
  #cartSectionIds() {
    return [
      ...new Set(
        Array.from(document.querySelectorAll('cart-items-component'))
          .map((node) => node.dataset.sectionId)
          .filter(Boolean)
      ),
    ];
  }

  /** Tier-1 shampoo add (user-initiated, from the open drawer). */
  async #add(variantId, button) {
    if (button.dataset.loading) return;
    button.dataset.loading = 'true';
    button.classList.add('is-loading');

    const sectionIds = this.#cartSectionIds();
    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', '1');
    if (sectionIds.length) body.append('sections', sectionIds.join(','));
    body.append('sections_url', window.location.pathname);

    try {
      const res = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (data.status) {
        button.dataset.loading = '';
        button.classList.remove('is-loading');
        return;
      }
      document.dispatchEvent(
        new CartAddEvent({}, 'shampoo-upsell', { source: 'shampoo-upsell', variantId: String(variantId), sections: data.sections })
      );
    } catch {
      button.dataset.loading = '';
      button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('shampoo-upsell')) {
  customElements.define('shampoo-upsell', ShampooUpsell);
}
